/**
 * Payment Gate Component
 * Stripe Elements integration for project deposits
 * "Pay-to-Play" - Project not created until payment succeeds
 *
 * L0-CMD-2026-0120-007 - Stripe Elements Implementation
 */

import React, { useState, useEffect } from 'react';
import {
  Elements,
  PaymentElement,
  useStripe,
  useElements,
} from '@stripe/react-stripe-js';
import { loadStripe, Stripe } from '@stripe/stripe-js';

// Initialize Stripe with publishable key
const stripePromise = loadStripe(
  process.env.REACT_APP_STRIPE_PUBLISHABLE_KEY || ''
);

interface PaymentGateProps {
  projectId: number;
  depositAmount: number;
  pageFee: number;
  totalDue: number;
  projectName: string;
  tier: string;
  estimatedHours: number;
  hourlyRate: number;
  invoiceId?: number;
  onPaymentSuccess: (paymentIntentId: string) => void;
  onCancel: () => void;
}

// Inner form component that uses Stripe hooks
interface CheckoutFormProps extends PaymentGateProps {
  clientSecret: string;
}

const CheckoutForm: React.FC<CheckoutFormProps> = ({
  projectId,
  depositAmount,
  pageFee,
  totalDue,
  projectName,
  tier,
  estimatedHours,
  hourlyRate,
  onPaymentSuccess,
  onCancel,
  clientSecret
}) => {
  const stripe = useStripe();
  const elements = useElements();
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [paymentStatus, setPaymentStatus] = useState<'idle' | 'processing' | 'success' | 'failed'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!stripe || !elements) {
      setError('Payment system not ready. Please refresh and try again.');
      return;
    }

    setProcessing(true);
    setError(null);
    setPaymentStatus('processing');

    try {
      const { error: submitError, paymentIntent } = await stripe.confirmPayment({
        elements,
        confirmParams: {
          return_url: `${window.location.origin}/payment/complete?project=${projectId}`,
        },
        redirect: 'if_required',
      });

      if (submitError) {
        setError(submitError.message || 'Payment failed. Please try again.');
        setPaymentStatus('failed');
        setProcessing(false);
        return;
      }

      if (paymentIntent) {
        if (paymentIntent.status === 'succeeded') {
          setPaymentStatus('success');
          onPaymentSuccess(paymentIntent.id);
        } else if (paymentIntent.status === 'processing') {
          setPaymentStatus('processing');
          // Payment is being processed, will be confirmed via webhook
          onPaymentSuccess(paymentIntent.id);
        } else if (paymentIntent.status === 'requires_action') {
          // 3D Secure or other authentication required
          // Stripe will handle the redirect automatically
          setError('Additional authentication required. Please complete the verification.');
          setPaymentStatus('idle');
          setProcessing(false);
        } else {
          setError(`Payment status: ${paymentIntent.status}. Please try again.`);
          setPaymentStatus('failed');
          setProcessing(false);
        }
      }
    } catch (err) {
      console.error('Payment error:', err);
      setError('An unexpected error occurred. Please try again.');
      setPaymentStatus('failed');
      setProcessing(false);
    }
  };

  if (paymentStatus === 'success') {
    return (
      <div className="payment-gate success">
        <div className="success-icon">&#10003;</div>
        <h2>Payment Successful!</h2>
        <p>Your project is now in the queue.</p>
        <div className="payment-details">
          <div className="detail-row">
            <span>Amount Paid:</span>
            <strong>${totalDue.toFixed(2)}</strong>
          </div>
          <div className="detail-row">
            <span>Project:</span>
            <strong>{projectName}</strong>
          </div>
        </div>
        <style>{successStyles}</style>
      </div>
    );
  }

  return (
    <div className="payment-gate">
      <div className="payment-header">
        <h2>Secure Payment</h2>
        <p>Complete your deposit to start your project</p>
      </div>

      <div className="payment-summary">
        <h3>Order Summary</h3>
        <div className="summary-row">
          <span>Project:</span>
          <strong>{projectName}</strong>
        </div>
        <div className="summary-row">
          <span>Service Tier:</span>
          <strong>{tier.toUpperCase()}</strong>
        </div>
        <div className="summary-row">
          <span>Estimated Hours:</span>
          <strong>{estimatedHours} hrs @ ${hourlyRate}/hr</strong>
        </div>

        <div className="divider"></div>

        <div className="summary-row">
          <span>Deposit (50%):</span>
          <strong>${depositAmount.toFixed(2)}</strong>
        </div>
        <div className="summary-row">
          <span>Page Processing Fee:</span>
          <strong>${pageFee.toFixed(2)}</strong>
        </div>

        <div className="divider"></div>

        <div className="summary-row total">
          <span>Total Due Now:</span>
          <strong>${totalDue.toFixed(2)}</strong>
        </div>
      </div>

      <form className="payment-form" onSubmit={handleSubmit}>
        <div className="stripe-element-container">
          <PaymentElement
            options={{
              layout: 'tabs',
              paymentMethodOrder: ['card'],
              defaultValues: {
                billingDetails: {
                  name: '',
                }
              }
            }}
          />
        </div>

        {error && (
          <div className="error-message">
            {error}
          </div>
        )}

        <div className="form-actions">
          <button type="button" className="btn-cancel" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="submit"
            className="btn-pay"
            disabled={!stripe || processing}
          >
            {processing ? (
              <>
                <span className="spinner"></span>
                Processing...
              </>
            ) : (
              <>Pay ${totalDue.toFixed(2)}</>
            )}
          </button>
        </div>

        <div className="security-note">
          <span className="lock-icon">&#128274;</span>
          Secured by Stripe. Your payment info is encrypted.
        </div>
      </form>

      <style>{formStyles}</style>
    </div>
  );
};

// Main component that wraps with Elements provider
const PaymentGate: React.FC<PaymentGateProps> = (props) => {
  const [clientSecret, setClientSecret] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  const API_BASE = process.env.REACT_APP_API_URL || '';

  useEffect(() => {
    createPaymentIntent();
  }, [props.projectId, props.totalDue]);

  const createPaymentIntent = async () => {
    setLoading(true);
    setError(null);

    try {
      const token = localStorage.getItem('access_token');

      const response = await fetch(`${API_BASE}/api/payments/create-intent`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          project_id: props.projectId,
          amount: props.totalDue,
          deposit_amount: props.depositAmount,
          page_fee_amount: props.pageFee,
          invoice_id: props.invoiceId
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to initialize payment');
      }

      const data = await response.json();
      setClientSecret(data.client_secret);
    } catch (err) {
      console.error('Payment init error:', err);
      setError(err instanceof Error ? err.message : 'Failed to initialize payment');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="payment-gate loading">
        <div className="loading-spinner"></div>
        <p>Initializing secure payment...</p>
        <style>{loadingStyles}</style>
      </div>
    );
  }

  if (error) {
    return (
      <div className="payment-gate error-state">
        <div className="error-icon">!</div>
        <h3>Payment Error</h3>
        <p>{error}</p>
        <div className="form-actions">
          <button className="btn-cancel" onClick={props.onCancel}>
            Cancel
          </button>
          <button className="btn-retry" onClick={createPaymentIntent}>
            Try Again
          </button>
        </div>
        <style>{errorStyles}</style>
      </div>
    );
  }

  if (!clientSecret) {
    return (
      <div className="payment-gate error-state">
        <div className="error-icon">!</div>
        <h3>Unable to Initialize Payment</h3>
        <p>Please try again or contact support.</p>
        <div className="form-actions">
          <button className="btn-cancel" onClick={props.onCancel}>
            Cancel
          </button>
          <button className="btn-retry" onClick={createPaymentIntent}>
            Try Again
          </button>
        </div>
        <style>{errorStyles}</style>
      </div>
    );
  }

  return (
    <Elements
      stripe={stripePromise}
      options={{
        clientSecret,
        appearance: {
          theme: 'night',
          variables: {
            colorPrimary: '#2563eb',
            colorBackground: '#1e293b',
            colorText: '#f1f5f9',
            colorTextSecondary: '#94a3b8',
            colorDanger: '#ef4444',
            fontFamily: 'Inter, system-ui, sans-serif',
            borderRadius: '8px',
            spacingUnit: '4px',
          },
          rules: {
            '.Input': {
              backgroundColor: '#334155',
              border: '1px solid #475569',
            },
            '.Input:focus': {
              borderColor: '#2563eb',
              boxShadow: '0 0 0 3px rgba(37, 99, 235, 0.2)',
            },
            '.Label': {
              color: '#94a3b8',
              fontSize: '13px',
              fontWeight: '500',
            },
            '.Tab': {
              backgroundColor: '#334155',
              border: '1px solid #475569',
            },
            '.Tab--selected': {
              backgroundColor: '#2563eb',
              borderColor: '#2563eb',
            },
          }
        },
      }}
    >
      <CheckoutForm {...props} clientSecret={clientSecret} />
    </Elements>
  );
};

// Styles
const formStyles = `
  .payment-gate {
    background: var(--bg-primary, #0f172a);
    border-radius: 12px;
    padding: 32px;
    max-width: 500px;
    margin: 0 auto;
  }

  .payment-header {
    text-align: center;
    margin-bottom: 24px;
  }

  .payment-header h2 {
    margin: 0 0 8px 0;
    font-size: 24px;
    color: var(--text-primary, #f1f5f9);
  }

  .payment-header p {
    margin: 0;
    color: var(--text-secondary, #94a3b8);
  }

  .payment-summary {
    background: var(--bg-secondary, #1e293b);
    border-radius: 8px;
    padding: 20px;
    margin-bottom: 24px;
  }

  .payment-summary h3 {
    margin: 0 0 16px 0;
    font-size: 16px;
    font-weight: 600;
    color: var(--text-primary, #f1f5f9);
  }

  .summary-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    font-size: 14px;
    color: var(--text-secondary, #94a3b8);
  }

  .summary-row strong {
    color: var(--text-primary, #f1f5f9);
  }

  .summary-row.total {
    font-size: 18px;
    font-weight: 700;
    margin-top: 8px;
  }

  .summary-row.total strong {
    color: var(--primary, #2563eb);
  }

  .divider {
    height: 1px;
    background: var(--border-color, #334155);
    margin: 12px 0;
  }

  .payment-form {
    margin-top: 24px;
  }

  .stripe-element-container {
    margin-bottom: 24px;
    min-height: 150px;
  }

  .error-message {
    background: rgba(239, 68, 68, 0.1);
    border: 1px solid #ef4444;
    color: #ef4444;
    padding: 12px;
    border-radius: 8px;
    margin-bottom: 16px;
    font-size: 14px;
  }

  .form-actions {
    display: flex;
    gap: 12px;
    margin-top: 24px;
  }

  .btn-cancel, .btn-pay, .btn-retry {
    flex: 1;
    padding: 14px;
    border: none;
    border-radius: 8px;
    font-size: 16px;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
  }

  .btn-cancel {
    background: var(--bg-tertiary, #334155);
    color: var(--text-primary, #f1f5f9);
  }

  .btn-cancel:hover {
    background: #475569;
  }

  .btn-pay {
    background: var(--primary, #2563eb);
    color: white;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
  }

  .btn-pay:hover:not(:disabled) {
    background: #1d4ed8;
  }

  .btn-pay:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }

  .btn-retry {
    background: var(--primary, #2563eb);
    color: white;
  }

  .btn-retry:hover {
    background: #1d4ed8;
  }

  .spinner {
    width: 16px;
    height: 16px;
    border: 2px solid white;
    border-top-color: transparent;
    border-radius: 50%;
    animation: spin 1s linear infinite;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }

  .security-note {
    text-align: center;
    font-size: 12px;
    color: var(--text-secondary, #94a3b8);
    margin-top: 16px;
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 6px;
  }

  .lock-icon {
    font-size: 14px;
  }
`;

const successStyles = `
  .payment-gate.success {
    background: var(--bg-primary, #0f172a);
    border-radius: 12px;
    padding: 48px;
    max-width: 500px;
    margin: 0 auto;
    text-align: center;
  }

  .success-icon {
    width: 80px;
    height: 80px;
    border-radius: 50%;
    background: #10b981;
    color: white;
    font-size: 48px;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 24px;
  }

  .payment-gate.success h2 {
    margin: 0 0 8px 0;
    color: var(--text-primary, #f1f5f9);
  }

  .payment-gate.success p {
    color: var(--text-secondary, #94a3b8);
    margin: 0 0 24px 0;
  }

  .payment-details {
    background: var(--bg-secondary, #1e293b);
    border-radius: 8px;
    padding: 20px;
    text-align: left;
  }

  .detail-row {
    display: flex;
    justify-content: space-between;
    margin-bottom: 8px;
    color: var(--text-secondary, #94a3b8);
  }

  .detail-row strong {
    color: var(--text-primary, #f1f5f9);
  }
`;

const loadingStyles = `
  .payment-gate.loading {
    background: var(--bg-primary, #0f172a);
    border-radius: 12px;
    padding: 48px;
    max-width: 500px;
    margin: 0 auto;
    text-align: center;
  }

  .loading-spinner {
    width: 48px;
    height: 48px;
    border: 3px solid var(--bg-tertiary, #334155);
    border-top-color: var(--primary, #2563eb);
    border-radius: 50%;
    animation: spin 1s linear infinite;
    margin: 0 auto 16px;
  }

  .payment-gate.loading p {
    color: var(--text-secondary, #94a3b8);
    margin: 0;
  }

  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

const errorStyles = `
  .payment-gate.error-state {
    background: var(--bg-primary, #0f172a);
    border-radius: 12px;
    padding: 48px;
    max-width: 500px;
    margin: 0 auto;
    text-align: center;
  }

  .error-icon {
    width: 64px;
    height: 64px;
    border-radius: 50%;
    background: rgba(239, 68, 68, 0.2);
    color: #ef4444;
    font-size: 32px;
    font-weight: bold;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
  }

  .payment-gate.error-state h3 {
    margin: 0 0 8px 0;
    color: var(--text-primary, #f1f5f9);
  }

  .payment-gate.error-state p {
    color: var(--text-secondary, #94a3b8);
    margin: 0 0 24px 0;
  }

  .form-actions {
    display: flex;
    gap: 12px;
    justify-content: center;
  }

  .btn-cancel, .btn-retry {
    padding: 12px 24px;
    border: none;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 600;
    cursor: pointer;
  }

  .btn-cancel {
    background: var(--bg-tertiary, #334155);
    color: var(--text-primary, #f1f5f9);
  }

  .btn-retry {
    background: var(--primary, #2563eb);
    color: white;
  }
`;

export default PaymentGate;
